import { Card, Descriptions, Divider, Grid, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type BasicSigningModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/BasicSigningModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import BasicSigningModuleActions from './BasicSigningModuleActions';

export type BasicSigningModuleProps = { module: BasicSigningModuleWithPermissionsSpecsFragment };

const BasicSigningModulePage = ({ module }: BasicSigningModuleProps) => {
    const { t } = useTranslation(['basicSigningModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('basicSigningModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('basicSigningModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<BasicSigningModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('basicSigningModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <Divider>{t('basicSigningModuleDetails:dividers.OTP')}</Divider>
            <Card title={t('basicSigningModuleDetails:card.title.mainDetails')}>
                <Typography.Paragraph>{t('basicSigningModuleDetails:card.description.lifeTime')}</Typography.Paragraph>
                <Descriptions size="small" bordered>
                    <Descriptions.Item label={t('basicSigningModuleDetails:content.lifeTime')}>
                        {module.lifeTime}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('basicSigningModuleDetails:content.transportProtocol')}>
                        {module.transportProtocol}
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        </ConsolePageWithHeader>
    );
};

export default BasicSigningModulePage;
